import { db } from '@/data/firebase/config';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { Audience, AudienceFormData } from '@/domain/types/audience';
import { User } from '@/domain/types/user';
import { userFromDoc } from '@/domain/use-cases/audiences/user-from-doc';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

export async function listAudiences(): Promise<Audience[]> {
  const snapshot = await getDocs(collection(db, DOCUMENTS_DB.AUDIENCES));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Audience);
}

export async function createAudience(
  data: AudienceFormData,
  createdBy: string,
): Promise<string> {
  const ref = await addDoc(collection(db, DOCUMENTS_DB.AUDIENCES), {
    ...data,
    createdAt: serverTimestamp(),
    createdBy,
  });
  return ref.id;
}

export async function updateAudience(
  id: string,
  data: Partial<AudienceFormData>,
): Promise<void> {
  await updateDoc(doc(db, DOCUMENTS_DB.AUDIENCES, id), data);
}

/**
 * Lo normal es desactivar una audiencia, no eliminarla: borrarla dejaría
 * publicaciones y usuarios apuntando a un ID inexistente, es decir, contenido
 * que nadie podría volver a administrar. La eliminación solo se permite cuando
 * nadie la usa (ver getDeletionBlock).
 */
export async function deactivateAudience(id: string): Promise<void> {
  await updateDoc(doc(db, DOCUMENTS_DB.AUDIENCES, id), { isActive: false });
}

/** Elimina el documento. Quien llama debe haber comprobado con getDeletionBlock. */
export async function deleteAudience(id: string): Promise<void> {
  await deleteDoc(doc(db, DOCUMENTS_DB.AUDIENCES, id));
}

export async function listMembers(audienceId: string): Promise<User[]> {
  const snapshot = await getDocs(
    query(
      collection(db, DOCUMENTS_DB.USERS),
      where('audienceIds', 'array-contains', audienceId),
    ),
  );
  return snapshot.docs.map(userFromDoc);
}

/** Cuántos usuarios pertenecen a la audiencia, sin descargar sus documentos. */
export async function countMembers(audienceId: string): Promise<number> {
  const snapshot = await getCountFromServer(
    query(
      collection(db, DOCUMENTS_DB.USERS),
      where('audienceIds', 'array-contains', audienceId),
    ),
  );
  return snapshot.data().count;
}

export async function addMember(
  userId: string,
  audienceId: string,
): Promise<void> {
  await updateDoc(doc(db, DOCUMENTS_DB.USERS, userId), {
    audienceIds: arrayUnion(audienceId),
  });
}

// Firestore admite 500 operaciones por lote.
const BATCH_LIMIT = 500;

/** Agrega la audiencia a varios usuarios; cada lote se escribe de forma atómica. */
export async function addMembers(
  userIds: string[],
  audienceId: string,
): Promise<void> {
  for (let start = 0; start < userIds.length; start += BATCH_LIMIT) {
    const batch = writeBatch(db);
    for (const userId of userIds.slice(start, start + BATCH_LIMIT)) {
      batch.update(doc(db, DOCUMENTS_DB.USERS, userId), {
        audienceIds: arrayUnion(audienceId),
      });
    }
    await batch.commit();
  }
}

export async function removeMember(
  userId: string,
  audienceId: string,
): Promise<void> {
  await updateDoc(doc(db, DOCUMENTS_DB.USERS, userId), {
    audienceIds: arrayRemove(audienceId),
  });
}

/**
 * Cuántas publicaciones referencian esta audiencia. Se usa para advertir antes
 * de desactivarla.
 */
export async function countPublicationsUsing(
  audienceId: string,
): Promise<number> {
  const snapshot = await getDocs(
    query(
      collection(db, DOCUMENTS_DB.RELEASES),
      where('audienceIds', 'array-contains', audienceId),
    ),
  );
  return snapshot.size;
}
